import {
  Point,
  Size,
  Rectangle,
} from "../../../domain/drawing";

export interface ITextAreaRectangle {
  caret: Rectangle;
  characters: ITextAreaCharacter[];
}

export interface ITextAreaCharacter {
  rectangle: Rectangle;
  beginOfLine: boolean;
  endOfLine: boolean;
  verticalIndex: number;
}

export class TextAreaUtility {
  // caretposition.js(MITライセンス)を参考に同様のアルゴリズムで実装.
  // なお、DOM取得のためにidを使用している箇所があったがコンポーネントとして問題があるため、classで取得できるよう修正した.
  // また、各文字の位置情報を取得できるようメソッドを修正した.
  // 実DOM操作してるので利用時は利用シーンに注意する事.
  public static getRectangles(element: HTMLTextAreaElement): ITextAreaRectangle {
    if (!element || !element.parentNode) {
      return { caret: new Rectangle(new Point(1, 0), new Size(1, 0)), characters: [] };
    }
    const dummy = this.getDummyElement(element);
    // Set scroll amount to dummy textarea.
    dummy.scrollLeft = element.scrollLeft;
    dummy.scrollTop = element.scrollTop;
    
    // Set code strings.
    const value = element.value;
    
    // Get caret character position.
    const leftText = value.slice(0, element.selectionStart);
    const selText = (element.selectionStart !== element.selectionEnd)
      ? value.slice(element.selectionStart, element.selectionEnd)
      : ""; // 不要のため削除
    const rightText = value.slice(element.selectionEnd, value.length);
    
    const leftProcessText = this.processText(leftText);
    const rightProcessText = this.processText(rightText);

    // Set calculation text for in dummy text area.
    // 文字の配置対応のため設定位置を変更
    dummy.firstElementChild.innerHTML = (leftProcessText !== "" ? leftProcessText + "<wbr>" : "")
                    + "<span class=\""
                    + element.className + "_i\">"
                    + "<wbr></span>" // 高さが取れない場合があるためwbrを追加
                    + this.processText(selText) // 範囲選択中の位置が正しく取れないため
                    + "<wbr>"
                    + (rightProcessText !== "" ? rightProcessText + "<wbr>" : "")
                    + "<span><wbr></span>";  // 終端の位置情報を示すDOMを追加
    
    // Get caret absolutely pixel position.
    const dummyRectangle = this.getElementRectangle(dummy);
    const caretElement = element.parentNode.querySelector<HTMLElement>("." + element.className + "_i");
    if (!caretElement) {
      return { caret: new Rectangle(new Point(1, 0), new Size(1, 0)), characters: [] };
    }

    // Get position of all characters.
    // 各文字の位置情報を取得
    const elements = element.parentNode.querySelectorAll<HTMLElement>("." + this.createDummyElementClassName(element) + " span:not(." + element.className + "_i)");
    const characters: ITextAreaCharacter[] = [];
    let endOfLine = true;
    let verticalIndex = -1;
    let lastY: number = null;
    for (let i = 0; i < elements.length; i++) {
      const elementItem = elements.item(i);
      const elementRectangle = this.getElementRectangle(elementItem);
      const characterRectangle = new Rectangle(
        elementRectangle.topLeft.subtract(dummyRectangle.topLeft),
        new Size(elementRectangle.width, elementRectangle.height));
      const beginOfLine = endOfLine;
      endOfLine = !!elementItem.firstElementChild;  // 終端 or 改行(br)
      if (lastY !== characterRectangle.y) {
        lastY = characterRectangle.y;
        verticalIndex++;
      }
      characters.push({
        rectangle: characterRectangle,
        beginOfLine,
        endOfLine,
        verticalIndex
      });
    }

    const caretRectangle = this.getElementRectangle(caretElement);
    return {
      caret: new Rectangle(
        caretRectangle.topLeft.subtract(dummyRectangle.topLeft),
        new Size(1, caretRectangle.height)),
      characters: characters
    };
  }

  // Set keyed text.
  private static processText(text: string) {
    // Get array of [Character reference] or [Character] or [NewLine].
    const m = this.escapeHTML(text).match(/((&amp;|&lt;|&gt;|&#34;|&#39;)|.|\n)/g);
    return m
      // 位置情報取得のため文字をspanで括る
      ? m.map(v => "<span>" + v + "</span>").join("<wbr>").replace(/\n/g, "<br>")
      : "";
  }

  // HTML Sanitizer
  private static escapeHTML(source: string) {
    const obj = document.createElement("pre");
    obj[typeof obj.textContent !== "undefined" ? "textContent" : "innerText"] = source;
    return obj.innerHTML;
  }

  private static getDummyElement(element: HTMLTextAreaElement): HTMLElement {
    // 疑似要素やブラウザ固有の機能はCSSにする必要があるため追加(一意ではないため他でも使用することがあれば対応が必要)
    const style = document.head.querySelector<HTMLStyleElement>("." + this.createDummyElementClassName(element));
    if (!style) {
      this.addDummyStyleElement(element);
    }
    const result = element.parentNode.querySelector<HTMLElement>("." + this.createDummyElementClassName(element));
    // CSS更新に対応
    return !!result
      ? this.updateDummyElement(result, element)
      : this.createDummyElement(element);
  }

  private static addDummyStyleElement(element: HTMLTextAreaElement): void {
    const style = document.createElement("style");
    style.setAttribute("type", "text/css");
    style.className = this.createDummyElementClassName(element);
    style.innerHTML = "\
    ." + style.className + " {\
      scrollbar-width: none;\
    }\
    ." + style.className + "::-webkit-scrollbar {\
      display: none;\
    }";
    document.head.appendChild(style);
  }

  private static createDummyElement(element: HTMLTextAreaElement): HTMLElement {
    const result = document.createElement("div");
    result.className = this.createDummyElementClassName(element);
    const inner = document.createElement("div");
    result.appendChild(inner);
    element.parentNode.appendChild(result);
    return this.updateDummyElement(result, element);
  }

  private static updateDummyElement(result: HTMLElement, element: HTMLTextAreaElement): HTMLElement {
    // wrap==="off"は単一行として扱う
    const inner = result.firstElementChild as HTMLElement;
    const style = this.getStyle(element);
    result.style.cssText = style.toString();
    // Fix for browser differece.
    // isWordWrap自動変数更新する実装は見てる箇所がないので削除.
    if (element.wrap === "off") {
      // chrome, firefox wordwrap=off
      result.style.overflow = "auto";
      result.style.whiteSpace = "pre";
      inner.style.display = "flex";
      inner.style.height = style.height;
      inner.style.alignItems = "flex-start";
    } else if (element.wrap !== undefined) {
      // firefox wordwrap=on
      if (this.getStyle(element).wordWrap === "break-word") {
        result.style.overflowY = "auto";
      }
    }
    result.style.visibility = "hidden";
    result.style.position = "absolute";
    result.style.top = "0px";
    result.style.left = "0px";
    // Firefox Support
    result.style.width = "100%";
    result.style.height = "100%";
    result.style.lineHeight = style.lineHeight;
    result.style.fontSize = style.fontSize;
    result.style.backgroundColor = style.backgroundColor;
    result.style.fontFamily = style.fontFamily;
    result.style.padding = style.padding;
    result.style.paddingTop = style.paddingTop;
    result.style.paddingRight = style.paddingRight;
    result.style.paddingBottom = style.paddingBottom;
    result.style.paddingLeft = style.paddingLeft;
    // サイズ計算に影響するため追加
    result.style.border = style.border;
    result.style.borderTop = style.borderTop;
    result.style.borderRight = style.borderRight;
    result.style.borderBottom = style.borderBottom;
    result.style.borderLeft = style.borderLeft;
    result.style.boxSizing = style.boxSizing;
    // 連続したスペースがあった場合問題があるので、ここまでに設定されていなければ追加する
    if (result.style.whiteSpace === "") {
      result.style.whiteSpace = style.whiteSpace;
    }
    // 文字の配置対応のため追加
    result.style.display = "flex";
    inner.style.width = result.clientWidth === result.scrollWidth
      ? "100%"
      : "";
    const textAlign = inner.clientWidth <= result.clientWidth
      ? style.textAlign
      : "left";
    inner.style.textAlign = textAlign;
    inner.style.justifyContent = textAlign;
    inner.style.alignSelf = inner.clientHeight <= result.clientHeight
      ? style.alignSelf
      : "flex-start";
    return result;
  }

  private static getStyle(element: HTMLTextAreaElement): CSSStyleDeclaration {
    return (element as any).currentStyle
      || document.defaultView.getComputedStyle(element, "");
  }


  private static createDummyElementClassName(element: HTMLTextAreaElement) {
    return element.className + "_dummyToGetCaretLocation";
  }

  // Get element absolute position
  private static getElementRectangle(element: HTMLElement): Rectangle {
    // Get scroll amount.
    const html = document.documentElement;
    const body = document.body;
    const scrollAmount = new Point(
      (body.scrollLeft || html.scrollLeft),
      (body.scrollTop || html.scrollTop));

    // Adjust "IE 2px bugfix" and scroll amount.
    const rectangle = element.getBoundingClientRect();
    return new Rectangle(
      new Point(
        rectangle.left - html.clientLeft + scrollAmount.x,
        rectangle.top - html.clientTop + scrollAmount.y),
      new Size(
        rectangle.right - rectangle.left,
        rectangle.bottom - rectangle.top
      )
    );
  }
}
